import React, { useState } from 'react';
import { Global, css } from '@emotion/react';
import useForceUpdateOnWindowResize from '@zola/zola-ui/src/hooks/useForceUpdateOnWindowResize';

// Types
import type {
  WThemeGroupFontFamilyView,
  WPublicWeddingView,
  WThemeComponentColorView,
} from '@zola/svc-web-api-ts-client';
import type { BaseLayoutProps } from 'components/publicWebsiteV2/pageLayouts/types';

// Components
import SiteHeader from 'components/publicWebsiteV2/common/Header';
import ModalRoot from 'components/common/ModalRoot';

// Util
import {
  ThemeContext,
  DEFAULT_HEADER_FONT_VALUES,
  DEFAULT_BODY_FONT_VALUES,
  DEFAULT_ACCENT_COLOR,
  DEFAULT_BACKGROUND_COLOR,
} from 'components/publicWebsiteV2/context';
import {
  mapButtonStylesToComponent,
  mapLinkStylesToComponent,
  mapBackgroundToCSS,
  fillFontProperties,
  mapFullFontViewToSerializedStyles,
} from 'components/publicWebsiteV2/util/mappers';
import { mapModeThemeToInputField } from 'components/publicWebsiteV2/util/genericFormElements.styles';
import { isDarkColor } from 'pages/publicStyleUtils/utils.styles';
import UnifiedNav from 'components/common/header/UnifiedNav';
import Metadata from 'components/publicWebsiteV2/common/Metadata';
import { mapWeddingAccountToWebsiteDateProp } from 'util/websiteDateUtil';

import fontAssets from 'cards/stylesheets/_fonts.styles';
import { Container } from './PublicV2.styles';

type PublicV2Props = BaseLayoutProps & {
  className?: string;
  inPreview?: boolean;
};

const PublicV2: React.FC<PublicV2Props> = ({ wedding, inPreview, children, className }) => {
  useForceUpdateOnWindowResize();
  const { title, nav_items, slug, city, state_province } = wedding || {};
  const websiteDate = wedding && mapWeddingAccountToWebsiteDateProp(wedding, undefined);
  // font families for theme
  const globalHeaderFontFamily = wedding?.public_theme_v2?.theme_group_font_families
    .HEADER as WThemeGroupFontFamilyView;
  const globalBodyFontFamily = wedding?.public_theme_v2?.theme_group_font_families
    .BODY as WThemeGroupFontFamilyView;

  const {
    GLOBAL: globalComponent,
    CONTENT_CONTAINER_HOME: contentContainerHomeComponent,
    CONTENT_CONTAINER: contentContainerComponent,
    NAV: navComponent,
    CMS_ENTITY: cmsEntityComponent,
    FIXED_WIDTH_CONTAINER: fixedWidthContainerComponent,
  } = wedding?.public_theme_v2?.components || {};

  // properties from global component
  const { fonts, background, accent_color: accentColor } = globalComponent || {};
  const globalHeaderFont = fonts?.HEADER?.[1];
  const globalBodyFont = fonts?.BODY?.[1];
  const backgroundColor = background?.background_color;

  // create data structure to hold global fallback values
  // supplements values if global not provided
  const globalHeaderFontValues = fillFontProperties(DEFAULT_HEADER_FONT_VALUES, {
    ...globalHeaderFontFamily,
    ...globalHeaderFont,
  });
  const globalHeaderFontStyle = mapFullFontViewToSerializedStyles(globalHeaderFontValues);

  const globalBodyFontValues = fillFontProperties(DEFAULT_BODY_FONT_VALUES, {
    ...globalBodyFontFamily,
    ...globalBodyFont,
  });
  const globalBodyFontStyle = mapFullFontViewToSerializedStyles(globalBodyFontValues);

  // Fill in fallbacks for background color to store in context
  const globalBgColor = backgroundColor?.value || DEFAULT_BACKGROUND_COLOR;
  const navBgColor = navComponent?.background?.background_color?.value;
  const homepageBgColor = contentContainerHomeComponent?.background?.background_color?.value;
  const contentContainerBgColor = contentContainerComponent?.background?.background_color?.value;
  const fixedWidthBgColor = fixedWidthContainerComponent?.background?.background_color?.value;
  const backgroundColors = {
    nav: navBgColor || globalBgColor,
    homepageContent: fixedWidthBgColor || homepageBgColor || globalBgColor,
    content: fixedWidthBgColor || contentContainerBgColor || globalBgColor,
    global: globalBgColor,
  };

  const cmsEntityComponentHeaderFontValues = fillFontProperties(
    globalHeaderFontValues,
    cmsEntityComponent?.fonts?.HEADER?.[1]
  );
  const cmsEntityComponentHeaderFontStyles = mapFullFontViewToSerializedStyles(
    cmsEntityComponentHeaderFontValues
  );
  const cmsEntityComponentBodyFontValues = fillFontProperties(
    globalBodyFontValues,
    cmsEntityComponent?.fonts?.BODY?.[1]
  );
  const cmsEntityComponentBodyFontStyles = mapFullFontViewToSerializedStyles(
    cmsEntityComponentBodyFontValues
  );
  const isFixedWidthContainerDarkMode = isDarkColor(backgroundColors.content);
  // input field to be used within fixed width container (not homepage)
  const globalInputFieldStyles = mapModeThemeToInputField(
    isFixedWidthContainerDarkMode,
    cmsEntityComponentBodyFontValues.color
  );
  const Button = mapButtonStylesToComponent(globalBodyFontFamily?.font_family?.name, accentColor);
  const CmsEntityBodyLink = mapLinkStylesToComponent(
    cmsEntityComponentBodyFontValues,
    isFixedWidthContainerDarkMode,
    backgroundColor as WThemeComponentColorView
  );
  const [context] = useState({
    components: {
      globalHeaderFontStyle,
      globalBodyFontStyle,
      globalHeaderFontValues,
      globalBodyFontValues,
      globalAccentColor: accentColor || DEFAULT_ACCENT_COLOR,
      globalInputFieldStyles,
      backgroundColors,
      Button,
      CmsEntityBodyLink,
      cmsEntityComponentHeaderFontValues,
      cmsEntityComponentBodyFontValues,
      cmsEntityComponentHeaderFontStyles,
      cmsEntityComponentBodyFontStyles,
    },
    highlightedComponents: [],
    wedding,
    inPreview,
  });

  const globalStyles = css`
    html {
      body {
        ${fontAssets}

        .modal-xl {
          width: 1020px;
        }
      }
    }
  `;

  return (
    <Container id="public-v2" className={className} background={mapBackgroundToCSS(background)}>
      <Metadata wedding={wedding as WPublicWeddingView} />
      <Global styles={globalStyles} />
      {!inPreview && <UnifiedNav isHidden />}
      {/* @ts-ignore */}
      <ModalRoot />
      <ThemeContext.Provider value={context}>
        <SiteHeader
          title={title}
          navItems={nav_items}
          slug={slug}
          websiteDate={websiteDate}
          city={city}
          stateProvince={state_province}
        />
        {children}
      </ThemeContext.Provider>
    </Container>
  );
};

export default PublicV2;
